import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * useCellSelection
 * Owns the cell-selection state machine: the active cell, the selection
 * rectangle (anchor + focus), and the selection "type" (cell / row / col).
 * Exposes click handlers for cells, row headers, and column headers along
 * with Shift / Ctrl modifier semantics that match Excel's behaviour.
 */
export default function useCellSelection({ normalizedRows, maxCols }) {
  const [selectedCell, setSelectedCell] = useState({ row: 0, col: 0 });
  const [selection, setSelection] = useState({
    anchor: { row: 0, col: 0 },
    focus: { row: 0, col: 0 },
    dragging: false,
  });
  const [selectionType, setSelectionType] = useState("cell");

  // End drag-select on mouseup anywhere on the document.
  useEffect(() => {
    if (!selection.dragging) return undefined;
    const onMouseUp = () => setSelection((prev) => ({ ...prev, dragging: false }));
    window.addEventListener("mouseup", onMouseUp);
    return () => window.removeEventListener("mouseup", onMouseUp);
  }, [selection.dragging]);

  const normalizedSelection = useMemo(() => {
    const r1 = Math.min(selection.anchor.row, selection.focus.row);
    const r2 = Math.max(selection.anchor.row, selection.focus.row);
    const c1 = Math.min(selection.anchor.col, selection.focus.col);
    const c2 = Math.max(selection.anchor.col, selection.focus.col);
    return { r1, r2, c1, c2 };
  }, [selection.anchor, selection.focus]);

  const startSelection = useCallback((row, col, event) => {
    const isShift = event.shiftKey;
    const isToggle = event.ctrlKey || event.metaKey;
    setSelectionType("cell");

    if (isShift) {
      setSelection((prev) => ({ anchor: prev.anchor, focus: { row, col }, dragging: true }));
    } else {
      setSelection({ anchor: { row, col }, focus: { row, col }, dragging: !isToggle });
    }
    setSelectedCell({ row, col });
  }, []);

  const extendSelection = useCallback((row, col) => {
    setSelection((prev) => (prev.dragging ? { ...prev, focus: { row, col } } : prev));
  }, []);

  const selectColumn = useCallback((colIndex, event) => {
    const totalRows = normalizedRows.length;
    const isShift = event.shiftKey;
    const isToggle = event.ctrlKey || event.metaKey;

    if (isShift && selectionType === "col") {
      setSelection((prev) => ({ anchor: prev.anchor, focus: { row: totalRows - 1, col: colIndex }, dragging: false }));
    } else if (isToggle && selectionType === "col") {
      setSelection({ anchor: { row: 0, col: colIndex }, focus: { row: totalRows - 1, col: colIndex }, dragging: false });
    } else {
      setSelection({ anchor: { row: 0, col: colIndex }, focus: { row: totalRows - 1, col: colIndex }, dragging: false });
    }
    setSelectionType("col");
    setSelectedCell({ row: 0, col: colIndex });
  }, [normalizedRows.length, selectionType]);

  const selectRow = useCallback((rowIndex, event) => {
    const isShift = event.shiftKey;
    const isToggle = event.ctrlKey || event.metaKey;

    if (isShift && selectionType === "row") {
      setSelection((prev) => ({ anchor: prev.anchor, focus: { row: rowIndex, col: maxCols - 1 }, dragging: false }));
    } else if (isToggle && selectionType === "row") {
      setSelection({ anchor: { row: rowIndex, col: 0 }, focus: { row: rowIndex, col: maxCols - 1 }, dragging: false });
    } else {
      setSelection({ anchor: { row: rowIndex, col: 0 }, focus: { row: rowIndex, col: maxCols - 1 }, dragging: false });
    }
    setSelectionType("row");
    setSelectedCell({ row: rowIndex, col: 0 });
  }, [maxCols, selectionType]);

  return {
    selectedCell, setSelectedCell,
    selection, setSelection,
    selectionType, setSelectionType,
    normalizedSelection,
    startSelection, extendSelection,
    selectColumn, selectRow,
  };
}
